import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import PricingCard from "../components/PricingCard";
import { AuthLayout } from "../../../auth";
import { usePlusSubscription } from "../../application/usePlusSubscription";
import {
    MAX_MONTHLY_CENTS,
    PLUS_MONTHLY_CENTS,
    PUBLISHED_MODULES,
    annualCents,
    formatEuros,
} from "../../domain/modulePrices";
import {
    moduleName,
    toolName,
    toolShortDescription,
} from "../../../../shared/i18n/toolCopy";

export default function PricingPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const language = i18n.language || "es";
    // The first subscription starts with a free trial; the buttons say so
    // only for an account that still has it.
    const { data: plus } = usePlusSubscription();
    const trialDays = plus && plus.subscription === null && plus.plan !== "plus" ? plus.trialDays : 0;

    const handleSelectFree = () => {
        // Logic for free plan selection (e.g. just continue)
        navigate("/");
    };

    // The Plus card used to be a disabled button and a "contact us" label,
    // because Plus was granted by hand. It now leads to the account's own
    // subscription page, where it can actually be bought, cancelled and
    // resumed. Whether this deployment can sell it is decided there, against
    // the API, rather than guessed here.
    const handleSelectPlus = () => {
        navigate("/subscription/billing");
    };

    // Max is bought on the same screen; the link only says which size.
    const handleSelectMax = () => {
        navigate("/subscription/billing?tier=max");
    };



    const freeFeatures = (t("subscription.free.features", { returnObjects: true }) as string[]).map(
        (text) => ({ text, included: true })
    );

    const plusFeatures = (t("subscription.plus.features", { returnObjects: true }) as string[]).map(
        (text) => ({ text, included: true })
    );

    const maxFeatures = (t("subscription.max.features", { returnObjects: true }) as string[]).map(
        (text) => ({ text, included: true })
    );

    return (
        <div className="min-h-screen w-full relative bg-gray-50 dark:bg-gray-900 overflow-y-auto">
            <AuthLayout hideLogo={false} hideVisualPanel={true} wrapperClassName="max-w-screen-2xl">
                <div className="w-full mx-auto px-4 py-12 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h1 className="text-3xl font-semibold tracking-tight text-content-primary sm:text-4xl">
                            {t("subscription.title")}
                        </h1>
                        <p className="mt-4 text-lg text-content-secondary">
                            {t("subscription.subtitle")}
                        </p>
                        <p className="mt-2">
                            <button
                                onClick={() => navigate("/subscription/compare")}
                                className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 hover:underline"
                            >
                                {t("subscription.compare.link", "View full comparison")} →
                            </button>
                        </p>
                    </div>

                    <div className="grid gap-8 lg:grid-cols-3 lg:gap-8 w-full mx-auto">
                        {/* Free Plan */}
                        <PricingCard
                            title={t("subscription.free.title")}
                            description={t("subscription.free.description")}
                            price={t("subscription.free.price")}
                            features={freeFeatures}
                            buttonText={t("subscription.free.button")}
                            onSelect={handleSelectFree}
                            variant="outline"
                        />

                        {/* Plus Plan */}
                        <PricingCard
                            title={t("subscription.plus.title")}
                            description={t("subscription.plus.description")}
                            price={formatEuros(PLUS_MONTHLY_CENTS, language)}
                            period={t("subscription.period")}
                            features={plusFeatures}
                            taxNote={t("subscription.billing.tax.suffix")}
                            buttonText={
                                trialDays > 0
                                    ? t("subscription.billing.trial.start", { days: trialDays })
                                    : t("subscription.plus.button")
                            }
                            footerText={t("subscription.plus.modulesNote")}
                            isPopular={true}
                            badge={t("subscription.plus.popularBadge")}
                            onSelect={handleSelectPlus}
                            variant="primary"
                        />

                        {/* Max: the same plan, sized for a fuller practice. */}
                        <PricingCard
                            title={t("subscription.max.title")}
                            description={t("subscription.max.description")}
                            price={formatEuros(MAX_MONTHLY_CENTS, language)}
                            period={t("subscription.period")}
                            features={maxFeatures}
                            taxNote={t("subscription.billing.tax.suffix")}
                            buttonText={
                                trialDays > 0
                                    ? t("subscription.billing.trial.startMax", { days: trialDays })
                                    : t("subscription.max.button")
                            }
                            footerText={t("subscription.plus.modulesNote")}
                            onSelect={handleSelectMax}
                            variant="outline"
                            isDeep
                        />
                    </div>

                    {/* What is billed on top of Plus. Said here, with the
                        price, rather than left for the catalogue: the plan's
                        price alone reads as everything included. */}
                    <section className="mx-auto mt-12 w-full max-w-4xl">
                        <h2 className="text-xl font-semibold text-content-primary">
                            {t("subscription.plus.modules.title")}
                        </h2>
                        <p className="mt-1 text-sm text-content-secondary">
                            {t("subscription.plus.modules.subtitle")}
                        </p>
                        <ul className="mt-4 divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
                            {PUBLISHED_MODULES.map((module) => (
                                <li
                                    key={module.slug}
                                    className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6"
                                >
                                    <div className="min-w-0">
                                        <p className="font-medium text-content-primary">
                                            {moduleName(t, { slug: module.slug })}
                                        </p>
                                        <p className="text-sm text-content-tertiary">
                                            {toolShortDescription(t, { slug: module.slug })}
                                        </p>
                                        {module.includes && (
                                            <p className="mt-1 text-xs font-medium text-brand-600 dark:text-brand-400">
                                                {t("subscription.plus.modules.includes", {
                                                    tools: [module.slug, ...module.includes]
                                                        .map((slug) => toolName(t, { slug }))
                                                        .join(" + "),
                                                })}
                                            </p>
                                        )}
                                    </div>
                                    <div className="shrink-0 sm:text-right">
                                        <p className="font-semibold text-content-primary">
                                            {t("subscription.plus.modules.perMonth", {
                                                price: formatEuros(module.monthlyCents, language),
                                            })}{" "}
                                            <span className="text-xs font-normal text-content-tertiary">
                                                {t("subscription.billing.tax.suffix")}
                                            </span>
                                        </p>
                                        <p className="text-xs text-content-tertiary">
                                            {t("subscription.plus.modules.perYear", {
                                                price: formatEuros(
                                                    annualCents(module.monthlyCents),
                                                    language
                                                ),
                                            })}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-3 text-xs text-content-tertiary">
                            {t("subscription.billing.tax.note")}
                        </p>
                    </section>
                </div>
            </AuthLayout>
        </div>
    );
}
